import { WEB_STATES } from '@constants/common'
import env from '@core/config/env'
import {
  checkCompanyGroup,
  canUseTokenOnWebState,
  getAuthenticatedHomePath,
  getTokenAccessSurface,
  getUser,
  logout,
  saveToken as clientLogin
} from '@core/utils'
import { isEmpty } from 'lodash'
import React, { useCallback, useMemo } from 'react'
import jwtDecode from 'jwt-decode'

const withAuth =
  ({ grant }) =>
  (PageComponent) => {
    return ({ location, history, match, ...props }) => {
      const [displayPage, setDisplayPage] = React.useState(false)
      const [userData, setUserData] = React.useState()
      const [permission, setPermission] = React.useState()

      const usePermissionMap = useCallback((perm) => {
        setPermission(perm?.items || {})
      }, [])

      const searchParams = useMemo(
        () => new URLSearchParams(location.search),
        [location.search]
      )

      const checkPublicPage = React.useCallback(
        (webState) => {
          const pathName = location.pathname

          const splitPathName = pathName.split('/')
          // fix after
          const isRootPage = pathName === '/'
          const isLoginPage = Boolean(
            splitPathName.length === 2 && splitPathName[1]
          )
          const isApproveEmailPage = Boolean(
            splitPathName.length === 3 &&
              !splitPathName[2].includes('approve-email') &&
              !splitPathName[2] === 'setting'
          )

          const isChangePasswordPage =
            splitPathName.length === 4 && splitPathName[3] === 'change-password'
          const isForgotPasswordPage =
            splitPathName.length === 4 && splitPathName[3] === 'forgot-password'

          const isChangePasswordAdminPage =
            splitPathName.length === 3 && splitPathName[2] === 'change-password'
          const isForgotPasswordAdminPage =
            splitPathName.length === 3 && splitPathName[2] === 'forgot-password'

          const globalIsPublic =
            isRootPage ||
            isLoginPage ||
            isChangePasswordPage ||
            isForgotPasswordPage ||
            isChangePasswordAdminPage ||
            isForgotPasswordAdminPage ||
            isApproveEmailPage

          // check public page by web state
          // TODO: right now there is not differences in public page
          if (webState === WEB_STATES.MAIN) {
            return globalIsPublic
          } else {
            return globalIsPublic
          }
        },
        [location.pathname]
      )

      const publicAuth = React.useCallback(
        (user) => {
          if (isEmpty(user)) {
            setDisplayPage(true)
          } else if (!canUseTokenOnWebState(user, env.WEB_STATE)) {
            const tokenSurface = getTokenAccessSurface(user)
            const appUrl =
              tokenSurface === 'main'
                ? env.APP_URL_EMPLOYEE
                : tokenSurface === 'admin'
                ? env.APP_URL_ADMIN
                : env.WEB_STATE === WEB_STATES.MAIN
                ? env.APP_URL_EMPLOYEE
                : env.APP_URL_ADMIN
            return logout({
              outSite: `${appUrl}/${user?._pis?.companyGroupCode}`
            })
          } else {
            if (env.WEB_STATE === WEB_STATES.MAIN) {
              history.replace(
                `/${match.params.companyGroup}/${user.companyId}/dashboard`
              )
            } else {
              history.replace(
                `/${match.params.companyGroup}/self-service/login`
              )
            }
          }
        },
        [history, match.params.companyGroup]
      )

      const privateAuth = React.useCallback(
        (user) => {
          const search = location.search
          const token = new URLSearchParams(search).get('token')
          if (token && !localStorage.getItem('redirect-url')) {
            localStorage.setItem(
              'redirect-url',
              `${history?.location?.pathname}${history?.location?.search}`
            )
          }
          if (isEmpty(user)) {
            const appUrl =
              env.WEB_STATE === WEB_STATES.MAIN
                ? env.APP_URL_EMPLOYEE
                : env.APP_URL_ADMIN
            window.location.replace(`${appUrl}/${match.params.companyGroup}`)
          } else if (!canUseTokenOnWebState(user, env.WEB_STATE)) {
            const tokenSurface = getTokenAccessSurface(user)
            const appUrl =
              tokenSurface === 'main'
                ? env.APP_URL_EMPLOYEE
                : tokenSurface === 'admin'
                ? env.APP_URL_ADMIN
                : env.WEB_STATE === WEB_STATES.MAIN
                ? env.APP_URL_EMPLOYEE
                : env.APP_URL_ADMIN
            return logout({
              outSite: `${appUrl}/${user?._pis?.companyGroupCode}`
            })
          } else {
            checkCompanyGroup({
              historyCompanyGroup: match.params.companyGroup,
              userCompanyGroup: user?._pis?.companyGroupCode
            })
            setUserData(user)
            setDisplayPage(true)
          }
        },
        [
          location.search,
          match.params.companyGroup,
          history?.location?.pathname,
          history?.location?.search
        ]
      )

      const isPublicPage = React.useMemo(() => {
        return checkPublicPage(env.WEB_STATE)
      }, [checkPublicPage])

      const isLoginWithToken = useCallback(() => {
        const accessToken = searchParams.get('at')
        const refreshToken = searchParams.get('rt')
        const data = jwtDecode(accessToken)
        const tokenSurface = getTokenAccessSurface(data)

        if (!tokenSurface) {
          const appUrl =
            env.WEB_STATE === WEB_STATES.MAIN
              ? env.APP_URL_EMPLOYEE
              : env.APP_URL_ADMIN
          return window.location.replace(
            `${appUrl}/${match.params.companyGroup}`
          )
        }

        if (!canUseTokenOnWebState(data, env.WEB_STATE)) {
          const appUrl =
            tokenSurface === 'main' ? env.APP_URL_EMPLOYEE : env.APP_URL_ADMIN
          const approve = searchParams.get('approve')
          const query = new URLSearchParams({
            at: accessToken,
            rt: refreshToken,
            ...(approve && { approve })
          })

          return window.location.replace(`${appUrl}?${query.toString()}`)
        }

        clientLogin({
          accessToken,
          refreshToken
        })

        return history.push(getAuthenticatedHomePath(data))
      }, [history, match.params.companyGroup, searchParams])

      React.useEffect(() => {
        const user = getUser()
        // NOTE check param have at(access token) and rt(refresh token) mean this is come from hr admin login
        if (
          location.search &&
          searchParams.get('at') &&
          searchParams.get('rt')
        ) {
          if (searchParams.get('approve')) {
            localStorage.setItem('redirect-url', searchParams.get('approve'))
          }
          return isLoginWithToken(searchParams)
        }
        if (isPublicPage) {
          publicAuth(user)
        } else {
          privateAuth(user)
        }
      }, [
        isLoginWithToken,
        isPublicPage,
        location.search,
        privateAuth,
        publicAuth,
        searchParams
      ])

      React.useEffect(() => {
        if (userData) {
          usePermissionMap(userData.permission)
        }
        return () => {}
      }, [userData, usePermissionMap])
      return displayPage ? (
        <PageComponent
          userData={userData}
          permission={permission}
          grant={grant}
          {...{ location, history, match, ...props }}
        />
      ) : null
    }
  }

export default withAuth
